import { useEffect, useMemo, useState } from 'react'
import { supabase } from '../lib/supabase.js'
import { formatMoney } from '../lib/format.js'
import { AGENT_LABELS } from '../lib/agents.js'
import KitchenLoader from './KitchenLoader.jsx'

// AI Catering Quote Modal — opened from a catering inquiry to generate
// a first-draft quote in seconds. Builds a compact snapshot from the
// inquiry + the restaurant's items / recipes (passed in as props), fires
// generate-catering-quote, and renders the structured response. The
// manager can then "Apply to inquiry" which writes the suggested price
// back to the catering inquiry's estimated_total + notes.
export default function CateringQuoteModal({
  open,
  onClose,
  inquiry,        // catering_inquiries row
  items,          // [{ name, unit, cost_per_unit, ... }]
  onApplyToInquiry, // (patch) => Promise. Updates the inquiry with the suggested price + notes.
}) {
  const [phase, setPhase] = useState('idle') // idle | generating | ready | error
  const [error, setError] = useState(null)
  const [quote, setQuote] = useState(null)
  const [applying, setApplying] = useState(false)

  // Reset state every time the modal closes so a fresh open starts at idle.
  useEffect(() => {
    if (!open) {
      setPhase('idle')
      setError(null)
      setQuote(null)
      setApplying(false)
    }
  }, [open])

  // Compact snapshot for the AI. Includes the inquiry's headcount,
  // event details, plus a sampled menu (up to 30 items) so the model
  // can build line items out of the restaurant's actual catalog.
  const snapshot = useMemo(() => {
    if (!inquiry) return null
    return {
      inquiry: {
        contact_name: inquiry.contact_name,
        event_date:   inquiry.event_date,
        event_time:   inquiry.event_time,
        event_type:   inquiry.event_type,
        headcount:    inquiry.headcount,
        location:     inquiry.location,
        notes:        inquiry.notes,
      },
      menu_items: (items || [])
        .slice(0, 30)
        .map((i) => ({
          name: i.name,
          unit: i.unit,
          cost_per_unit: Number(i.cost_per_unit ?? 0),
          category: i.category,
        })),
    }
  }, [inquiry, items])

  if (!open || !inquiry) return null

  async function generate() {
    if (phase === 'generating') return
    setPhase('generating')
    setError(null)
    try {
      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL
      const anonKey     = import.meta.env.VITE_SUPABASE_ANON_KEY
      const { data: sess } = await supabase.auth.getSession()
      const token = sess?.session?.access_token || anonKey

      const resp = await fetch(`${supabaseUrl}/functions/v1/generate-catering-quote`, {
        method: 'POST',
        headers: {
          'content-type':  'application/json',
          'authorization': `Bearer ${token}`,
          'apikey':        anonKey,
        },
        body: JSON.stringify({ snapshot }),
      })
      if (!resp.ok) {
        const errText = await resp.text()
        throw new Error(`HTTP ${resp.status}: ${errText.slice(0, 200) || resp.statusText}`)
      }
      const data = await resp.json()
      if (data.error) {
        setError(data.message || 'AI could not generate the quote.')
        setPhase('error')
        return
      }
      setQuote(data)
      setPhase('ready')
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('[CateringQuoteModal] generate failed', e)
      setError('Could not generate — please try again in a moment.')
      setPhase('error')
    }
  }

  async function applyToInquiry() {
    if (applying || !quote) return
    setApplying(true)
    try {
      // Build a notes block from the AI quote so the manager can refine
      // it. Doesn't overwrite existing notes — appends.
      const aiNotes = [
        '— AI quote suggestion —',
        quote.headline,
        '',
        'Line items:',
        ...quote.line_items.map((li) => `  • ${li.name} — ${li.quantity} (${formatMoney(li.line_cost)})`),
        '',
        `Food cost: ${formatMoney(quote.total_food_cost)}`,
        `Suggested price: ${formatMoney(quote.suggested_price)}`,
        `Prep time: ${quote.prep_time_hours} hours`,
        '',
        'Notes for manager:',
        ...quote.notes_for_manager.map((n) => `  • ${n}`),
        '',
        'Confirm with customer:',
        ...quote.questions_for_customer.map((q) => `  • ${q}`),
      ].join('\n')
      const combinedNotes = inquiry.notes
        ? `${inquiry.notes}\n\n${aiNotes}`
        : aiNotes

      await onApplyToInquiry(
        {
          estimated_total: quote.suggested_price,
          notes:           combinedNotes,
        },
        quote, // full structured payload — persists to quote_payload (atomic with status='quoted' in CateringScreen)
      )
      onClose?.()
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('[CateringQuoteModal] apply failed', e)
      setError(e?.message || 'Could not apply to inquiry.')
    } finally {
      setApplying(false)
    }
  }

  const canGenerate = inquiry.headcount && Number(inquiry.headcount) > 0

  return (
    <div className="fixed inset-0 z-40">
      <button
        type="button"
        aria-label="Close"
        onClick={onClose}
        className="absolute inset-0 bg-black/50 animate-fade-in"
      />
      <div
        role="dialog"
        aria-modal="true"
        aria-label={AGENT_LABELS.cateringQuoteDraft}
        className="animate-sheet-up absolute inset-x-0 bottom-0 max-h-[92vh] overflow-y-auto rounded-t-2xl bg-white shadow-2xl"
      >
        <div className="sticky top-0 z-10 bg-white pt-2">
          <div className="mx-auto h-1.5 w-12 rounded-full bg-black/15" />
          <div className="flex items-center justify-between px-4 py-3">
            <h2 className="text-xl font-medium truncate">
              {AGENT_LABELS.cateringQuoteDraft}
            </h2>
            <button
              type="button"
              onClick={onClose}
              className="min-h-btn min-w-[48px] rounded-lg px-3 text-base font-medium text-black/70 hover:bg-black/5"
              aria-label="Close"
            >
              Close
            </button>
          </div>
          <div className="px-4 pb-3 text-xs text-black/60">
            For <span className="font-medium text-black/80">{inquiry.contact_name}</span>
            {inquiry.headcount ? ` · ${inquiry.headcount} guests` : ''}
            {inquiry.event_type ? ` · ${inquiry.event_type}` : ''}
          </div>
        </div>

        <div className="px-4 pb-4">
          {/* IDLE — pitch the feature + go button */}
          {phase === 'idle' && (
            <div className="rounded-xl border border-blue-200 bg-blue-50/40 p-4">
              <div className="text-sm text-black/80">
                Generate a first-draft quote based on this inquiry's headcount and event style.
                You'll get a menu breakdown, food cost estimate, suggested price, and a prep
                time — ready to refine.
              </div>
              {!canGenerate && (
                <div className="mt-3 rounded-md bg-amber-50 px-3 py-2 text-xs text-amber-900">
                  Add a <span className="font-medium">headcount</span> on the inquiry first —
                  the AI needs it to scale portions.
                </div>
              )}
              <button
                type="button"
                onClick={generate}
                disabled={!canGenerate}
                className="mt-3 w-full rounded-lg bg-blue-600 px-4 py-3 text-base font-medium text-white shadow-sm hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-50"
              >
                ✨ Generate quote
              </button>
              <div className="mt-1.5 text-[11px] text-black/50">
                Refine the result before sending to the customer.
              </div>
            </div>
          )}

          {phase === 'generating' && (
            <div className="rounded-xl border border-blue-200 bg-blue-50/40 p-6">
              <KitchenLoader
                messages={[
                  'Reading the inquiry\u2026',
                  'Portioning for the headcount\u2026',
                  'Pricing it for 30% food cost\u2026',
                  'Plating your quote\u2026',
                ]}
              />
            </div>
          )}

          {phase === 'error' && (
            <div className="rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-900">
              {error}
              <button
                type="button"
                onClick={generate}
                className="mt-3 w-full rounded-lg border border-red-300 bg-white px-3 py-2 text-sm font-medium text-red-700 hover:bg-red-50"
              >
                Try again
              </button>
            </div>
          )}

          {phase === 'ready' && quote && (
            <>
              {/* Headline */}
              <div className="rounded-xl border border-emerald-200 bg-emerald-50/50 p-4">
                <div className="text-[11px] font-medium text-emerald-900/70">
                  Proposed package
                </div>
                <div className="mt-1 text-base font-medium leading-snug text-emerald-900">
                  {quote.headline}
                </div>

                <div className="mt-3 grid grid-cols-3 gap-2 text-center">
                  <div className="rounded-lg bg-white px-2 py-2 shadow-sm">
                    <div className="text-[10px] font-medium text-black/55">Food cost</div>
                    <div className="text-base font-medium tabular-nums">{formatMoney(quote.total_food_cost)}</div>
                  </div>
                  <div className="rounded-lg bg-white px-2 py-2 shadow-sm">
                    <div className="text-[10px] font-medium text-black/55">Suggested</div>
                    <div className="text-base font-medium tabular-nums text-emerald-700">
                      {formatMoney(quote.suggested_price)}
                    </div>
                  </div>
                  <div className="rounded-lg bg-white px-2 py-2 shadow-sm">
                    <div className="text-[10px] font-medium text-black/55">Prep</div>
                    <div className="text-base font-medium tabular-nums">
                      {quote.prep_time_hours}h
                    </div>
                  </div>
                </div>

                {quote.target_food_cost_pct != null && (
                  <div className="mt-2 text-[11px] text-emerald-900/70">
                    Implied food cost: {Math.round(quote.target_food_cost_pct * 100) / 100}%
                  </div>
                )}
              </div>

              {/* Line items */}
              <section className="mt-4">
                <h3 className="mb-2 text-sm font-medium">Menu line items</h3>
                <ul className="overflow-hidden rounded-xl border border-black/10">
                  {quote.line_items.map((li, i) => (
                    <li key={i} className="border-b border-black/5 px-3 py-2.5 last:border-b-0">
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0 flex-1">
                          <div className="truncate text-sm font-medium">{li.name}</div>
                          <div className="text-xs text-black/60">
                            {li.quantity}
                            {li.notes ? ` · ${li.notes}` : ''}
                          </div>
                        </div>
                        <div className="text-right text-sm font-medium tabular-nums">
                          {formatMoney(li.line_cost)}
                        </div>
                      </div>
                    </li>
                  ))}
                </ul>
              </section>

              {/* Notes for manager */}
              {quote.notes_for_manager?.length > 0 && (
                <section className="mt-4">
                  <h3 className="mb-2 text-sm font-medium">Notes for you</h3>
                  <ul className="space-y-1.5 rounded-xl border border-black/10 bg-amber-50/40 px-3 py-2.5">
                    {quote.notes_for_manager.map((n, i) => (
                      <li key={i} className="flex items-start gap-2 text-sm text-black/85">
                        <span className="mt-1.5 inline-block h-1.5 w-1.5 shrink-0 rounded-full bg-amber-500" />
                        <span>{n}</span>
                      </li>
                    ))}
                  </ul>
                </section>
              )}

              {/* Questions for customer */}
              {quote.questions_for_customer?.length > 0 && (
                <section className="mt-4">
                  <h3 className="mb-2 text-sm font-medium">Confirm with the customer</h3>
                  <ul className="space-y-1.5 rounded-xl border border-black/10 bg-blue-50/40 px-3 py-2.5">
                    {quote.questions_for_customer.map((q, i) => (
                      <li key={i} className="flex items-start gap-2 text-sm text-black/85">
                        <span className="mt-1.5 inline-block h-1.5 w-1.5 shrink-0 rounded-full bg-blue-500" />
                        <span>{q}</span>
                      </li>
                    ))}
                  </ul>
                </section>
              )}

              {/* Actions */}
              <div className="mt-5 space-y-2">
                <button
                  type="button"
                  onClick={applyToInquiry}
                  disabled={applying}
                  className="min-h-btn w-full rounded-lg bg-emerald-600 px-4 py-3 text-base font-medium text-white shadow-sm hover:bg-emerald-700 disabled:opacity-50"
                >
                  {applying ? 'Saving…' : 'Apply to inquiry'}
                </button>
                <button
                  type="button"
                  onClick={generate}
                  disabled={phase === 'generating'}
                  className="w-full rounded-lg border border-black/15 bg-white px-3 py-2 text-sm font-medium text-black/70 hover:bg-black/5"
                >
                  Regenerate
                </button>
              </div>

              <div className="mt-3 text-[11px] text-black/50">
                Saving updates this inquiry's estimated total to the suggested price and appends
                the breakdown to its notes. You can still edit the inquiry afterwards.
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  )
}
